/**
 * ModelSelectorField Component
 *
 * Catalog-native model selector field. Wraps ModelSelect (self-fetches from
 * /catalog via useCatalogModels) so the model list is always in sync with the
 * unified model center rather than a stale appContext copy.
 *
 * Modality is derived from schema.optionsSource (chatModels → 'text', etc.).
 * featureToggle and linkedParameters are kept as-is (orthogonal to model pick).
 *
 * D5: linkedParameters panel migrated from static ModelParametersPanel to
 * catalog-driven ModelParameters (renders only params the model supports).
 */

import React, { useMemo, useCallback } from 'react';
import { ToggleLeft, ToggleRight } from 'lucide-react';
import { FieldSchema, ValidationResult, getConfigValue, MERGED_FIELD_CONFIG as FIELD_CONFIG, FieldType } from '@/config/fieldConfig';
import { ModelSelect } from '@/components/model-display/ModelSelect';
import { ModelParameters } from '@/components/model-display/ModelParameters';
import { ModelParametersPanel } from '@/components/ModelSelector';
import type { FeatureToggleProps, LinkedParameter } from '@/components/ModelSelector/types';
import type { Modality } from '@/types/models';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { useCatalogModels } from '@/lib/queries/useCatalogModels';
import { modelId as getModelId } from '@/lib/models/catalogAccessors';
import { isHookEnabled, getHook, type HookId } from '@/config/hooks-registry';

// ---------------------------------------------------------------------------
// Non-sampling linked param helpers (shared with ModelsSection.tsx logic)
// ---------------------------------------------------------------------------

const SAMPLING_PARAM_NAMES = new Set([
  'temperature', 'top_p', 'max_tokens', 'frequency_penalty', 'presence_penalty', 'reasoning_effort',
]);

function buildLeftoverLinkedParams(
  linkedParameters: string[] | undefined,
  dataSource: Record<string, unknown> | undefined,
  prefix: string,
): LinkedParameter[] {
  if (!linkedParameters?.length || !dataSource) return [];
  const result: LinkedParameter[] = [];
  for (const path of linkedParameters) {
    const paramName = path.replace(`${prefix}.`, '');
    if (SAMPLING_PARAM_NAMES.has(paramName)) continue;
    const paramConfig = FIELD_CONFIG[path];
    if (!paramConfig) continue;
    const rawLabel = typeof paramConfig.label === 'function' ? paramConfig.label() : paramConfig.label;
    const label = typeof rawLabel === 'string' ? rawLabel : '';
    let type: 'number' | 'select' | 'button_group' = 'number';
    if (paramConfig.type === FieldType.SELECT) {
      type = paramConfig.buttonGroup ? 'button_group' : 'select';
    }
    result.push({
      name: path,
      label,
      type,
      value: getConfigValue(dataSource, path),
      options: paramConfig.options,
      min: paramConfig.min,
      max: paramConfig.max,
      step: paramConfig.step,
    });
  }
  return result;
}

// ---------------------------------------------------------------------------
// optionsSource → Modality and capability sub-filter
// ---------------------------------------------------------------------------

const OPTIONSSOURCE_TO_MODALITY: Record<string, Modality> = {
  chatModels: 'text',
  visionModels: 'text',
  memoryModels: 'text',
  reasoningModels: 'text',
  imageModels: 'image',
  embeddingModels: 'embedding',
  sttModels: 'stt',
};

// Narrows text-modality slots by capability so an operator cannot accidentally
// pick a non-vision model into a vision slot (or non-reasoning into reasoning).
const OPTIONSSOURCE_TO_CAPABILITY: Record<string, 'vision' | 'reasoning'> = {
  visionModels: 'vision',
  reasoningModels: 'reasoning',
};

const FUNCTION_CALLING_MODEL_FIELDS = new Set([
  'models.chat.model_id',
  'models.chat.quick_model_id',
]);

function resolveModality(schema: FieldSchema): Modality | undefined {
  // Dynamic optionsSource can't be resolved statically; fall back to undefined
  // (useCatalogModels returns all models when modality is undefined)
  if (schema.getOptionsSource) return undefined;
  return schema.optionsSource ? OPTIONSSOURCE_TO_MODALITY[schema.optionsSource] : undefined;
}

function resolveCapabilityFilter(
  name: string,
  schema: FieldSchema,
): 'vision' | 'reasoning' | 'function_calling' | undefined {
  if (FUNCTION_CALLING_MODEL_FIELDS.has(name)) return 'function_calling';
  if (schema.getOptionsSource || !schema.optionsSource) return undefined;
  return OPTIONSSOURCE_TO_CAPABILITY[schema.optionsSource];
}

// ---------------------------------------------------------------------------
// Feature toggle button (DS-compliant)
// ---------------------------------------------------------------------------

function FeatureToggleButton({
  featureToggle,
  disabled,
}: {
  featureToggle: FeatureToggleProps;
  disabled: boolean;
}) {
  const { language } = useI18n();
  const enabled = featureToggle.featureEnabled;

  return (
    <button
      type="button"
      onClick={() => !disabled && featureToggle.onFeatureToggle(!enabled)}
      disabled={disabled}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
        enabled
          ? 'bg-primary/10 text-primary hover:bg-primary/20'
          : 'bg-muted text-muted-foreground hover:bg-muted/80',
        disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
      )}
    >
      {enabled ? (
        <>
          <ToggleRight className="w-3.5 h-3.5" aria-hidden />
          <span>{language === 'zh' ? '已启用' : 'Enabled'}</span>
        </>
      ) : (
        <>
          <ToggleLeft className="w-3.5 h-3.5" aria-hidden />
          <span>{language === 'zh' ? '已禁用' : 'Disabled'}</span>
        </>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ModelSelectorFieldProps {
  name: string;
  value: string | null;
  onChange: (value: string | null) => void;
  schema: FieldSchema;
  error?: ValidationResult;
  disabled?: boolean;
  appContext?: Record<string, unknown>;
  /** Full form data for reading/writing linked parameters */
  formData?: Record<string, unknown>;
  /** Callback to update form data (for linked parameters) */
  onFormDataChange?: (path: string, value: unknown) => void;
  /** Use compact inline display (minimal row instead of card) */
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ModelSelectorField: React.FC<ModelSelectorFieldProps> = ({
  name,
  value,
  onChange,
  schema,
  error,
  disabled = false,
  appContext: _appContext,
  formData,
  onFormDataChange,
  compact = false,
}) => {
  const { language } = useI18n();

  const label = typeof schema.label === 'function' ? schema.label() : schema.label;
  const help =
    schema.help && typeof schema.help !== 'object'
      ? typeof schema.help === 'function'
        ? schema.help()
        : schema.help
      : undefined;

  const modality = resolveModality(schema);
  const capabilityFilter = resolveCapabilityFilter(name, schema);

  // ---------------------------------------------------------------------------
  // D5: catalog-driven parameter panel
  // ---------------------------------------------------------------------------

  // Resolve the selected model against the text catalog (only text models have
  // sampling params; other modalities return null from ModelParameters anyway).
  const { data: textModels } = useCatalogModels('text');

  const catalogModel = useMemo(() => {
    if (compact || !schema.linkedParameters?.length || !value) return undefined;
    return textModels?.find((m) => getModelId(m) === value);
  }, [compact, schema.linkedParameters, textModels, value]);

  // Non-sampling linked params (e.g. language, budget_tokens) for the legacy panel.
  // Gated on !compact so the compact row (in ModelsSection) skips this — its parent
  // ModelRow handles leftover params directly via buildLeftoverLinkedParams.
  const leftoverLinkedParams = useMemo(
    () => buildLeftoverLinkedParams(
      schema.linkedParameters,
      formData,
      name.replace('.model_id', ''),
    ),
    [schema.linkedParameters, formData, name],
  );

  // Build param values keyed by param NAME (e.g. "temperature"), not full path.
  // The config paths in linkedParameters follow the pattern "<prefix>.<paramName>"
  // where prefix = name.replace('.model_id', '') (e.g. "models.chat").
  const paramValues = useMemo<Record<string, number | boolean | string>>(() => {
    if (!catalogModel || !schema.linkedParameters || !formData) return {};
    const prefix = name.replace('.model_id', '');
    const vals: Record<string, number | boolean | string> = {};
    for (const path of schema.linkedParameters) {
      const pName = path.replace(`${prefix}.`, '');
      const v = getConfigValue(formData, path);
      if (v !== null && v !== undefined) {
        vals[pName] = v as number | boolean | string;
      }
    }
    return vals;
  }, [catalogModel, schema.linkedParameters, formData, name]);

  // Feature toggle support for optional features (vision, stt, reasoning).
  // hasFeatureToggle is not set in any live schema today, so this block is
  // currently dead — the early return fires on every call.
  const featureToggle = useMemo<FeatureToggleProps | undefined>(() => {
    if (!schema.hasFeatureToggle || !schema.featureHookId || compact) return undefined;

    const hookId = schema.featureHookId as HookId;
    const hook = getHook(hookId);
    if (!hook) return undefined;

    const useSeparateEnabledFlag =
      schema.allowEmpty && hook.configPath.includes('.enabled');

    let featureEnabled: boolean;
    if (useSeparateEnabledFlag && formData) {
      featureEnabled = Boolean(getConfigValue(formData, hook.configPath));
    } else {
      featureEnabled = isHookEnabled(hookId, value);
    }

    return {
      featureEnabled,
      onFeatureToggle: (enabled: boolean) => {
        if (useSeparateEnabledFlag) {
          onFormDataChange?.(hook.configPath, enabled);
        } else {
          if (enabled) {
            const defaultModel =
              schema.defaultEnabledModel ||
              null;
            onChange(defaultModel);
          } else {
            onChange(null);
          }
        }
      },
      featureLabel: hook.displayName,
    };
  }, [
    schema.hasFeatureToggle,
    schema.featureHookId,
    schema.defaultEnabledModel,
    schema.allowEmpty,
    value,
    formData,
    compact,
    onChange,
    onFormDataChange,
  ]);

  // Compute effective allowEmpty
  const allowEmpty = schema.hasFeatureToggle
    ? schema.allowEmpty && featureToggle?.featureEnabled
    : schema.allowEmpty || !schema.required;

  const emptyLabel =
    schema.emptyLabel || (language === 'zh' ? '使用系统默认' : 'Use system default');

  const handleChange = useCallback(
    (id: string) => {
      onChange(id === '' ? null : id);
    },
    [onChange],
  );

  // D5: map param name → full config path (e.g. "temperature" → "models.chat.temperature")
  const handleParamChange = useCallback(
    (paramName: string, paramValue: number | boolean | string | null) => {
      const prefix = name.replace('.model_id', '');
      onFormDataChange?.(`${prefix}.${paramName}`, paramValue);
    },
    [name, onFormDataChange],
  );

  // Leftover params store the full config path as LinkedParameter.name, so we can
  // pass directly to onFormDataChange without prepending the prefix.
  const handleLeftoverParamChange = useCallback(
    (paramPath: string, paramValue: any) => {
      onFormDataChange?.(paramPath, paramValue);
    },
    [onFormDataChange],
  );

  // ModelSelect expects string (empty string = no selection)
  const selectValue = value || '';

  // Feature disabled → grey out the selector
  const selectorDisabled = disabled || (featureToggle ? !featureToggle.featureEnabled : false);

  return (
    <div className="field-wrapper">
      {compact ? (
        // Compact mode: small modal-trigger button, no label/params/toggle
        <ModelSelect
          value={selectValue}
          onChange={handleChange}
          modality={modality}
          capabilityFilter={capabilityFilter}
          variant="modal"
          allowEmpty={Boolean(allowEmpty)}
          emptyLabel={emptyLabel}
          disabled={disabled}
        />
      ) : (
        // Full mode: label row + optional feature toggle + selector + params
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <label className="text-sm font-medium text-foreground">{label}</label>
            {featureToggle && (
              <FeatureToggleButton featureToggle={featureToggle} disabled={disabled} />
            )}
          </div>

          {help && <p className="text-xs text-muted-foreground">{help}</p>}

          <ModelSelect
            value={selectValue}
            onChange={handleChange}
            modality={modality}
            capabilityFilter={capabilityFilter}
            variant="modal"
            allowEmpty={Boolean(allowEmpty)}
            emptyLabel={emptyLabel}
            disabled={selectorDisabled}
          />

          {catalogModel && (
            <div className="mt-2">
              <ModelParameters
                model={catalogModel}
                values={paramValues}
                parameterNames={schema.linkedParameters?.map((path) => path.split('.').pop()!)}
                onChange={handleParamChange}
              />
            </div>
          )}
          {leftoverLinkedParams.length > 0 && (
            <ModelParametersPanel
              parameters={leftoverLinkedParams}
              onParameterChange={handleLeftoverParamChange}
              compact
            />
          )}
        </div>
      )}

      {error && (
        <div className={`text-xs mt-1 ${error.isWarning ? 'text-yellow-600' : 'text-red-600'}`}>
          {error.message}
        </div>
      )}
    </div>
  );
};

export default ModelSelectorField;
